<template>
  <!--
    ContractDetailsComponent
    Úsalo como modal/drawer pasando contractId y contractType.
    Ejemplos:
      <ContractDetailsComponent :contract-id="123" contract-type="contract" />
      <ContractDetailsComponent :contract-id="456" contract-type="addendum" />
      <ContractDetailsComponent :contract-id="789" contract-type="subcontract" />
  -->
  <ContractEditorCore
    ref="core"
    :contract-id="contractId"
    :mode="contractType"
    :non-editable-component="config.nonEditableComponent"
    :editor-component="config.editorComponent"
    :show-start-date="config.showStartDate"
    :show-payment-validation="config.showPaymentValidation"
    :is-subcontract="config.isSubcontract"
    :fetch-params="config.fetchParams"
    :on-contract-loaded="config.onContractLoaded"
    :parent-contract-id-getter="config.parentContractIdGetter"
    :responsable-from-state="config.responsableFromState"
    :document-label="config.documentLabel"
    :from-modal="true"
    @open-cost-breakdown-modal="openCostBreakdownModal"
    @start-delete-contract="handleDeleteAddendum"
		@handle-contract-addendums="$emit('handle-contract-addendums', $event)"
  >
  </ContractEditorCore>
</template>

<script>
import ContractEditorCore from './ContractEditorCore.vue'

// Componentes condicionales: se importan todos pero solo se montan los necesarios
import NonEditableContract    from './NonEditableContract.vue'
import NonEditableSubcontract from './NonEditableSubcontract.vue'
import TextEditor             from '@/components/Helpers/Editor/TextEditor'
import SubcontractEditor      from '@/components/Helpers/Editor/SubcontractEditor'
// CostBreakdown modals moved to ContractEditorCore
import { deleteContractAddendum }          from '@/helpers/api/contracts'

// ─── Configuración por tipo ──────────────────────────────────────────────────

const TYPE_CONFIG = {
  contract: {
    nonEditableComponent:  NonEditableContract,
    editorComponent:       TextEditor,
    showStartDate:         true,
    showPaymentValidation: true,
    isSubcontract:         false,
    fetchParams:           {},
    responsableFromState:  'contract',
    documentLabel:         'Contrato',
    onContractLoaded:      null,
    parentContractIdGetter: null,
  },
  addendum: {
    nonEditableComponent:  NonEditableContract,
    editorComponent:       TextEditor,
    showStartDate:         false,
    showPaymentValidation: false,
    isSubcontract:         false,
    fetchParams:           { get_addendums: true },
    responsableFromState:  'addendum',
    documentLabel:         'Anexo',
    onContractLoaded:      null,          // se sobreescribe abajo en el método
    parentContractIdGetter: (c) => c?.addendum_to_contract_id,
  },
  subcontract: {
    nonEditableComponent:  NonEditableSubcontract,
    editorComponent:       SubcontractEditor,
    showStartDate:         true,
    showPaymentValidation: false,
    isSubcontract:         true,
    fetchParams:           {},
    responsableFromState:  'subcontract',
    documentLabel:         'Subcontrato',
    onContractLoaded:      null,
    parentContractIdGetter: (c) => c?.subcontract_to_contract_id,
  },
}

// ─── Componente ─────────────────────────────────────────────────────────────

export default {
  name: 'ContractDetailsComponent',

  components: {
    ContractEditorCore,
  },

  props: {
    contractId:   { type: [Number, String], required: true },
    contractType: {
      type:      String,
      default:   'contract',
      validator: (v) => ['contract', 'addendum', 'subcontract'].includes(v),
    },
  },

  computed: {
    config() {
      const base = TYPE_CONFIG[this.contractType] ?? TYPE_CONFIG.contract

      // Para el addendum, inyectamos el onContractLoaded dinámicamente
      if (this.contractType === 'addendum') {
        return { ...base, onContractLoaded: this.onAddendumLoaded }
      }
      return base
    },

    isContract()    { return this.contractType === 'contract' },
    isAddendum()    { return this.contractType === 'addendum' },
    isSubcontract() { return this.contractType === 'subcontract' },

    // Datos del core expuestos para los modales del slot
    coreContract() { return this.$refs.core?.contract   ?? null },
    coreProject()  { return this.$refs.core?.project    ?? null },
    costsTable()   { return this.$refs.core?.costsTable ?? null },
  },

  methods: {
    // Callback post-fetch para el anexo (guarda los addendums hermanos en el core)
    onAddendumLoaded(response) {
      if (this.$refs.core) {
        this.$refs.core.otherContractAddendums = response.parent_contract_addendums ?? null
      }
    },

    openCostBreakdownModal() {
      if (this.isContract) {
        this.$store.commit('costbreakdown/setCostBreakdownSummaryTablesModal', true)
      } else if (this.isSubcontract) {
        this.$store.commit('costbreakdown/setCostBreakdownSubcontractTablesModal', true)
      }
    },

    async handleDeleteAddendum() {
      if (!this.isAddendum) return
      const confirmed = await confirm('Eso eliminará también la Tabla de Costo Asociada. ¿Estás seguro de que deseas eliminar este anexo?')
      if (!confirmed) return

      const core     = this.$refs.core
      const contract = core?.contract
      if (!contract) return

      const result = await deleteContractAddendum({ contract_addendum_id: contract.id })
      if (result.code === 200) {
        // En modal: cerramos el modal del anexo y abrimos el contrato padre
        this.$store.commit('contracts/setContractAddendumComponentModalId', null)
        this.$store.commit('contracts/setContractComponentModalId', contract.addendum_to_contract_id)
        core.setMessage({ message: 'Se ha eliminado correctamente el anexo y su tabla de costo.', title: 'Anexo Eliminado', type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
      } else {
        core.setMessage({ message: 'Hubo un error en la operación, por favor intenta nuevamente', title: 'Ha ocurrido un error', type: 'danger', icon: 'mdi-close-octagon', color: '#FF1209' })
      }
    },
  },
}
</script>